import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, InputGroup, Dropdown, Modal, Spinner } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { adminService } from '../../services/adminService';
import PageModal from './PageModal';
import toast from 'react-hot-toast';
import { Pagination } from 'react-bootstrap';


const Pages: React.FC = () => {
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedPage, setSelectedPage] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewPage, setPreviewPage] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [totalRecords, setTotalRecords] = useState(0);


  const [totalPages, setTotalPages] = useState(1);


  useEffect(() => {
    loadPages();
  }, [currentPage, filterCategory, filterStatus]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadPages();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await adminService.getPages({
        search: searchTerm,
        type: filterCategory === 'all' ? undefined : filterCategory,
        status: filterStatus === 'all' ? undefined : filterStatus,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });




      if (response.success && response.data) {
        setPages(response.data);
        setTotalPages(
          Math.ceil((response.pagination?.total || 0) / itemsPerPage)
        );
        setTotalRecords(response.pagination?.total || 0);


      }



    } catch (error: any) {
      console.error('Error loading pages:', error);
      toast.error('Failed to load pages');
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredPages = pages;

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      'Published': 'success',
      'Draft': 'warning',
      'Archived': 'secondary'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getCategoryBadge = (category: string) => {
    const variants: Record<string, string> = {
      'Company': 'primary',
      'Legal': 'danger',
      'Support': 'info',
      'Media': 'warning'
    };
    return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleAddPage = () => {
    setModalMode('add');
    setSelectedPage(null);
    setShowModal(true);
  };

  const handleEditPage = (page: any) => {
    setModalMode('edit');
    setSelectedPage(page);
    setShowModal(true);
  };

  const handleViewPage = (page: any) => {
    setPreviewPage(page);
    setShowPreview(true);
  };

  const handleDeletePage = async (page: any) => {
    if (!window.confirm(`Are you sure you want to delete page "${page.title}"?`)) {
      return;
    }

    try {
      const response = await adminService.deletePage(page.id);
      if (response.success) {
        toast.success(`Page "${page.title}" deleted successfully!`);
        loadPages();
      }
    } catch (error: any) {
      toast.error('Failed to delete page');
    }
  };

  const handleSavePage = async (pageData: any) => {
    try {
      let response;
      if (modalMode === 'add') {
        response = await adminService.createPage(pageData);
        if (response.success) {
          toast.success('Page created successfully!');
        }
      } else {
        response = await adminService.updatePage(selectedPage.id, pageData);
        if (response.success) {
          toast.success('Page updated successfully!');
        }
      }
      setShowModal(false);
      loadPages();
    } catch (error: any) {
      toast.error(`Failed to ${modalMode === 'add' ? 'create' : 'update'} page`);
    }
  };

  return (
    <>
      <Helmet>
        <title>Pages - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Pages</h2>
            <p className="text-muted mb-0">Manage website pages and content</p>
          </div>
          <Button variant="primary" onClick={handleAddPage}>
            <i className="bi-plus-circle me-1"></i>
            Create Page
          </Button>
        </div>

        <Card>
          <Card.Header>
            <Row className="align-items-center">
              <Col>
                <h5 className="mb-0">
                  All Pages ({totalRecords})
                </h5>

              </Col>
              <Col md={2}>
                <Form.Select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="general">General</option>
                  <option value="company">Company</option>
                  <option value="legal">Legal</option>
                  <option value="support">Support</option>
                  {/* <option value="media">Media</option> */}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  {/* <option value="archived">Archived</option> */}
                </Form.Select>
              </Col>
              <Col md={3}>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search pages..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Title</th>
                  <th>Slug</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Last Modified</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPages.map((page) => (
                  <tr key={page.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{page.title}</div>
                        <small className="text-muted">{page.metaDescription}</small>
                      </div>
                    </td>
                    <td>
                      <code>/pages/{page.slug}</code>
                    </td>
                    <td>{getCategoryBadge(page.category)}</td>
                    <td>{getStatusBadge(page.status)}</td>
                    <td>{formatDate(page.lastModified || page.updatedAt)}</td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${page.id}`}>
                          <i className="bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleViewPage(page)}>
                            <i className="bi-eye me-2"></i>View Page
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleEditPage(page)}>
                            <i className="bi-pencil me-2"></i>Edit Page
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleViewPage(page)}>
                            <i className="bi-window me-2"></i>Preview
                          </Dropdown.Item>
                          <Dropdown.Divider />
                          <Dropdown.Item className="text-danger" onClick={() => handleDeletePage(page)}>
                            <i className="bi-trash me-2"></i>Delete Page
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>


        {!loading && totalPages > 1 && (

          <Card.Footer className="d-flex justify-content-center">
            <Pagination>
              <Pagination.Prev
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              />

              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index}
                  active={currentPage === index + 1}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}

              <Pagination.Next
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              />
            </Pagination>
          </Card.Footer>
        )}

        {/* Add/Edit Page Modal */}
        <PageModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onSave={handleSavePage}
          page={selectedPage}
          mode={modalMode}
        />
        {/* Preview Modal */}
        <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{previewPage?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <Badge bg="light" text="dark" className="me-2">
                <i className="bi-link-45deg me-1"></i>/pages/{previewPage?.slug}
              </Badge>
              {getCategoryBadge(previewPage?.category)}
              {getStatusBadge(previewPage?.status)}
            </div>
            <div className="mb-3">
              <small className="text-muted">Meta Description:</small>
              <p>{previewPage?.metaDescription}</p>
            </div>
            <hr />
            <div dangerouslySetInnerHTML={{ __html: previewPage?.content || '' }} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Close
            </Button>
            <Button variant="primary" onClick={() => {
              setShowPreview(false);
              handleEditPage(previewPage);
            }}>
              <i className="bi-pencil me-1"></i>Edit Page
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Pages;
